import { LinkButton } from '@cmg/common';
import { Stack } from '@cmg/design-system';
import React from 'react';
import { useDispatch } from 'react-redux';

import ServerErrorsBanner from '../../../../common/components/indicators/server-error/ServerErrorsBanner';
import NotAccessible from '../../../../common/components/layout/not-accessible/NotAccessible';
import { IndicationStatus } from '../../../../graphql';
import { useCmgEntityKeyContext } from '../../contexts/CmgEntityKeyContext';
import IndicationComplianceLoadingSkeleton from './compliance/IndicationComplianceLoadingSkeleton';
import { openCrmSelectionModal } from './components/crm-selection/CrmSelectModal';
import ManageSyndicateIndication from './components/manage-indication/ManageSyndicateIndication';
import { useIndicationActivitySyndicateContext } from './context/IndicationActivitySyndicateContext';
import { SBankInvestorKey } from './IndicationActivitySellSide.styles';
import { useIndicationCoverage } from './IndicationActivitySellSideHooks';

type Props = {
  displayMissingContacts: boolean;
  setDisplayMissingContacts: (val: boolean) => void;
};

const IndicationActivitySyndicate: React.FC<Props> = ({
  displayMissingContacts,
  setDisplayMissingContacts,
}) => {
  const {
    offeringId,
    access,
    error,
    basicPublishedOffering,
    allInvestorFirmsMappedToCmgEntityKey,
  } = useIndicationActivitySyndicateContext();

  const dispatch = useDispatch();
  const { cmgEntityKey } = useCmgEntityKeyContext();

  const isProspectusDocumentMissing = basicPublishedOffering?.prospectusDocuments.length === 0;

  const institutionalIndication = basicPublishedOffering?.orderBook.institutionalIndication;

  const { isCurrentInvestorCovered, loading: indicationCoverageLoading } = useIndicationCoverage(
    offeringId,
    cmgEntityKey,
    institutionalIndication?.duplicateIndicationIds ?? []
  );

  if (!access.canReadOfferingAndIndication) {
    return (
      <NotAccessible
        subject="Indication"
        content="You are not permitted to access this Indication"
      />
    );
  }

  const canEditIfDuplicate = institutionalIndication?.status !== IndicationStatus.Duplicate;

  // We need to create a way to handle editable in the parent component that will disable
  // all checks for inner components, editable checks should be moved here
  const isIndicationEditable = canEditIfDuplicate && isCurrentInvestorCovered;

  const { bankInvestorKey, cmgEntityName } = institutionalIndication?.investorInformation ?? {};

  const { id } = institutionalIndication ?? {};

  const hasDuplication =
    institutionalIndication?.duplicateIndicationIds &&
    institutionalIndication?.duplicateIndicationIds.length > 0;
  const displayEditBankInvestorKeyButton =
    hasDuplication ||
    bankInvestorKey ||
    allInvestorFirmsMappedToCmgEntityKey?.some(firm => firm.firmKey !== null);

  return (
    <Stack>
      {error && <ServerErrorsBanner error={error} />}

      {!!basicPublishedOffering && !!institutionalIndication && (
        <div>
          <SBankInvestorKey>Bank Investor Key: {bankInvestorKey ?? '-'}</SBankInvestorKey>
          {displayEditBankInvestorKeyButton && (
            <LinkButton
              testId="open-crm-modal-button"
              onClick={() =>
                dispatch(
                  openCrmSelectionModal({
                    cmgEntityKey,
                    offeringId,
                    indicationId: id,
                    cmgEntityName,
                    firmKey: bankInvestorKey,
                    duplicateIndicationIds: institutionalIndication?.duplicateIndicationIds,
                  })
                )
              }
            >
              Edit
            </LinkButton>
          )}
        </div>
      )}

      {indicationCoverageLoading && <IndicationComplianceLoadingSkeleton />}

      {!!basicPublishedOffering && !indicationCoverageLoading && (
        <ManageSyndicateIndication
          editable={isIndicationEditable}
          isInvestorCovered={isCurrentInvestorCovered}
          isProspectusDocumentMissing={isProspectusDocumentMissing}
          isOrderBookOpen={false}
          displayMissingContacts={displayMissingContacts}
          setDisplayMissingContacts={setDisplayMissingContacts}
        />
      )}
    </Stack>
  );
};

export default React.memo(IndicationActivitySyndicate);
